import { forwardRef, useEffect, useState } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { Error } from "../Error";
import { Help } from "../Help";
import { Label, labelSizes, labelVariants } from "../Label";
import { Suffix, suffixSizes } from "../Suffix";
import styles from "./TextArea.style";

const TextAreaStyled = styled.div((props) => ({ ...styles(props) }));

const TextArea = forwardRef(
    (
        {
            labelAlignLeft,
            labelWidth,
            labelComponent,
            labelIconComponent,
            labelIsMobileOnly,
            name,
            id,
            value,
            label,
            placeholder,
            rows,
            noResize,
            onBlur,
            onChange,
            onClick,
            onFocus,
            onKeyDown,
            onKeyUp,
            readOnly,
            required,
            disabled,
            error,
            errorMessage,
            helpText,
            variant,
            size,
            fieldWidth,
            inputWidth,
            actionComponent: Action,
            actionComponentRenderCondition,
            suffix,
            suffixWidth,
            className,
            isActionComponentTitle,
            ...rest
        },
        ref,
    ) => {
        const [focused, setFocused] = useState(false);

        // Blur won't fire when the disabled state is set on a focused input.
        useEffect(() => {
            if (disabled && focused) {
                setFocused(false);
                if (onBlur) {
                    onBlur();
                }
            }
        }, [disabled, focused, onBlur]);

        // onFocus
        const handleFocus = (event) => {
            if (disabled) {
                event.stopPropagation();
                return;
            }
            if (onFocus) {
                onFocus(event);
            }
            setFocused(true);
        };

        // onBlur
        const handleBlur = (event) => {
            if (onBlur) {
                onBlur(event);
            }
            setFocused(false);
        };

        // onChange
        const handleChange = (event, ...args) => {
            if (onChange) {
                onChange(event, ...args);
            }
        };

        return (
            <TextAreaStyled
                labelAlignLeft={labelAlignLeft}
                labelWidth={labelWidth}
                focused={focused}
                disabled={disabled}
                error={error}
                variant={variant}
                size={size}
                fieldWidth={fieldWidth}
                inputWidth={inputWidth}
                noResize={noResize}
                hasActionComponent={Action}
                isActionComponentTitle={isActionComponentTitle}
                className={className}
                {...rest}
            >
                <Label
                    className="label"
                    label={label}
                    labelComponent={labelComponent}
                    iconComponent={labelIconComponent}
                    id={id}
                    htmlFor={id}
                    error={error}
                    required={required}
                    focused={focused}
                    variant={labelVariants.PRIMARY}
                    size={labelSizes[size?.toUpperCase()]}
                    width={labelWidth}
                    alignLeft={labelAlignLeft}
                    isMobileOnly={labelIsMobileOnly}
                />
                <div className="inputContainer">
                    <div className="inputContainer_inner">
                        <div className="inputWrap">
                            <textarea
                                ref={ref}
                                name={name}
                                aria-invalid={!!error}
                                id={id}
                                value={value}
                                placeholder={placeholder}
                                rows={rows}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                onClick={onClick}
                                onKeyDown={onKeyDown}
                                onKeyUp={onKeyUp}
                                readOnly={readOnly}
                                required={required}
                                disabled={disabled}
                            />
                            {Action && !disabled && !!actionComponentRenderCondition && (
                                <div className="inputAction">
                                    <Action />
                                </div>
                            )}
                        </div>
                        <Suffix
                            label={suffix}
                            size={suffixSizes[size?.toUpperCase()]}
                            width={suffixWidth}
                            error={error}
                        />
                    </div>
                    <Error error={error} errorMessage={errorMessage} />
                    <Help text={helpText} />
                </div>
            </TextAreaStyled>
        );
    },
);

const textAreaVariants = Object.freeze({
    PRIMARY: "primary",
    SECONDARY: "secondary",
    TERTIARY: "tertiary",
    QUATERNARY: "quaternary",
});

const textAreaSizes = Object.freeze({
    NORMAL: "normal",
    // NOTE: extend sizes if needed
});

TextArea.propTypes = {
    labelAlignLeft: PropTypes.bool,
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    labelComponent: PropTypes.node,
    labelIconComponent: PropTypes.node,
    labelIsMobileOnly: PropTypes.bool,
    name: PropTypes.string,
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
    noResize: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    helpText: PropTypes.string,
    variant: PropTypes.oneOf([...Object.values(textAreaVariants)]),
    size: PropTypes.oneOf([...Object.values(textAreaSizes)]),
    fieldWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inputWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    actionComponent: PropTypes.elementType,
    actionComponentRenderCondition: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ]),
    suffix: PropTypes.string,
    suffixWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    isActionComponentTitle: PropTypes.bool,
};

TextArea.defaultProps = {
    labelAlignLeft: false,
    labelWidth: undefined,
    labelComponent: undefined,
    labelIconComponent: undefined,
    labelIsMobileOnly: false,
    name: undefined,
    id: undefined,
    value: undefined,
    label: undefined,
    placeholder: undefined,
    rows: 4,
    noResize: false,
    onBlur: () => {},
    onChange: () => {},
    onClick: () => {},
    onFocus: () => {},
    onKeyDown: () => {},
    onKeyUp: () => {},
    readOnly: false,
    required: false,
    disabled: false,
    error: false,
    errorMessage: undefined,
    helpText: undefined,
    variant: textAreaVariants.PRIMARY,
    size: textAreaSizes.NORMAL,
    fieldWidth: undefined,
    inputWidth: undefined,
    actionComponent: undefined,
    actionComponentRenderCondition: true,
    suffix: undefined,
    suffixWidth: undefined,
    className: undefined,
    isActionComponentTitle: false,
};

export { textAreaVariants, textAreaSizes };

export default TextArea;
